import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Rocket, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { TAG_COLORS, getDraftItems, saveDraftItems, clearDraftItems, getCurrentRelease } from '../../data/releaseNotes';

/**
 * ShipVersionModal — admin-only UI for composing release-note entries on-the-fly.
 * The composed entries are persisted to localStorage via saveDraftItems() and
 * prepended to the "What's New" carousel. When you're happy with them, copy the
 * JSON into /data/releaseNotes.js and bump APP_VERSION to commit them to code.
 */
const TAG_OPTIONS = ['NEW', 'FIX', 'POLISH', 'DEEP', 'LEGAL'];

export const ShipVersionModal = ({ isOpen, onClose }) => {
  const [items, setItems] = useState([]);
  const currentRelease = getCurrentRelease();

  useEffect(() => {
    if (isOpen) setItems(getDraftItems());
  }, [isOpen]);

  if (!isOpen) return null;

  const addItem = () => {
    setItems([...items, { tag: 'NEW', color: TAG_COLORS.NEW, title: '', desc: '' }]);
  };

  const updateItem = (idx, patch) => {
    const next = [...items];
    if (patch.tag) patch.color = TAG_COLORS[patch.tag] || TAG_COLORS.NEW;
    next[idx] = { ...next[idx], ...patch };
    setItems(next);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSaveDraft = () => {
    const cleaned = items.filter((it) => it.title?.trim() || it.desc?.trim());
    saveDraftItems(cleaned);
    toast.success(`${cleaned.length} draft ${cleaned.length === 1 ? 'entry' : 'entries'} saved — visible in What's New now`, { duration: 2500 });
    onClose();
  };

  const handleClearDrafts = () => {
    clearDraftItems();
    setItems([]);
    toast.success('All draft entries cleared', { duration: 1800 });
  };

  const handleCopyAsCode = () => {
    const cleaned = items.filter((it) => it.title?.trim() || it.desc?.trim());
    if (!cleaned.length) {
      toast.error('Nothing to copy yet — add an entry first');
      return;
    }
    const code = cleaned.map((it) => `      { tag: '${it.tag}', color: TAG_COLORS.${it.tag}, title: ${JSON.stringify(it.title)}, desc: ${JSON.stringify(it.desc)} },`).join('\n');
    navigator.clipboard.writeText(code).then(
      () => toast.success('Release-note JSON copied — paste into /data/releaseNotes.js', { duration: 3500 }),
      () => toast.error('Clipboard write failed')
    );
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
      data-testid="ship-version-modal"
    >
      <div
        className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Rocket className="w-4.5 h-4.5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-base font-bold">Ship Version</h2>
              <p className="text-[11px] text-muted-foreground">
                Current: <span className="font-mono">v{currentRelease.version}</span> · {currentRelease.codename}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-secondary"
            data-testid="ship-version-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <div className="flex items-start gap-2 p-2.5 rounded-md bg-sky-500/10 border border-sky-500/20">
            <Info className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-sky-300 leading-relaxed">
              Draft entries appear at the top of the "What's New" carousel immediately and persist in localStorage on this device. When you're happy with them, use <strong>Copy as Code</strong> and paste into <code className="font-mono bg-sky-500/15 px-1 rounded">/data/releaseNotes.js</code> to commit them to the build.
            </p>
          </div>

          {items.length === 0 && (
            <div className="text-center py-10 text-xs text-muted-foreground">
              No draft entries yet. Click <strong>Add Entry</strong> to compose a release note.
            </div>
          )}

          {items.map((it, idx) => (
            <div key={idx} className="p-3 rounded-md border border-border bg-secondary/30 space-y-2" data-testid={`ship-version-entry-${idx}`}>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {TAG_OPTIONS.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => updateItem(idx, { tag })}
                      className={`px-2 py-1 text-[10px] font-bold rounded transition-all ${
                        it.tag === tag ? 'ring-2 ring-offset-1 ring-offset-card' : 'opacity-50 hover:opacity-90'
                      }`}
                      style={{
                        background: `${TAG_COLORS[tag]}22`,
                        color: TAG_COLORS[tag],
                        borderColor: TAG_COLORS[tag],
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => removeItem(idx)}
                  className="ml-auto w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-500/15 text-red-400"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Title</Label>
                <Input
                  value={it.title}
                  onChange={(e) => updateItem(idx, { title: e.target.value })}
                  placeholder="e.g. One-Click Submit Cleanup"
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</Label>
                <Textarea
                  value={it.desc}
                  onChange={(e) => updateItem(idx, { desc: e.target.value })}
                  placeholder="Short plain-English description of what changed and why it matters."
                  rows={3}
                  className="text-sm resize-none"
                />
              </div>
            </div>
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={addItem}
            className="w-full"
            data-testid="ship-version-add-entry"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add Entry
          </Button>
        </div>

        <div className="p-3 border-t border-border flex items-center gap-2 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearDrafts}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            disabled={items.length === 0}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Clear All Drafts
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyAsCode}
            disabled={items.length === 0}
            data-testid="ship-version-copy-code"
          >
            Copy as Code
          </Button>
          <Button
            size="sm"
            onClick={handleSaveDraft}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="ship-version-save"
          >
            <Rocket className="w-3.5 h-3.5 mr-1.5" />
            Save &amp; Preview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShipVersionModal;
